/**
 * Vision 모드 상태 관리 스토어
 *
 * 사진 채점의 전체 플로우 상태를 관리
 * gameStore와 분리된 독립적인 상태 관리 (콤보/점수 없음, 다른 상태 머신)
 *
 * 플로우: idle → capturing → previewing → processing → reviewing → confirming → 완료
 */

import { create } from 'zustand';
import {
  VisionMode,
  VisionGradeResponse,
  VisionConfirmResponse,
  EditableAnswer,
  RecognizedAnswer,
  VisionProcessingStage,
  VisionError,
} from '../types/vision';
import { CompressionResult } from '../utils/imageCompression';

/**
 * Vision 스토어 상태 인터페이스
 */
interface VisionStoreState {
  // ===== 현재 모드 =====
  /** 현재 Vision 플로우 단계 */
  mode: VisionMode;

  // ===== 이미지 관련 =====
  /** 캡처된 이미지 파일 */
  capturedImage: File | Blob | null;

  /** 이미지 미리보기 URL (URL.createObjectURL로 생성) */
  imagePreviewUrl: string | null;

  // ===== API 응답 =====
  /** Vision API 채점 응답 (확정 전) */
  gradeResponse: VisionGradeResponse | null;

  /** 채점 확정 응답 (최종 결과) */
  confirmResponse: VisionConfirmResponse | null;

  // ===== 편집 상태 =====
  /** 학생이 수정 가능한 답안 목록 */
  editableAnswers: EditableAnswer[];

  // ===== 보류 상태 =====
  /** 채팅으로 풀기로 보류된 문제번호 목록 */
  deferredProblems: string[];

  // ===== 에러 상태 =====
  /** 에러 메시지 (null이면 에러 없음) */
  error: string | null;

  /** 분류된 에러 정보 (재시도/fallback 판단용) */
  lastError: VisionError | null;

  // ===== 처리 진행 상태 (Phase 5) =====
  /** 현재 처리 단계 (로딩 UI용) */
  processingStage: VisionProcessingStage | null;

  /** 재시도 횟수 */
  retryCount: number;

  /** 이미지 압축 결과 */
  compressionResult: CompressionResult | null;

  // ===== 페이지 정보 (SolvePage에서 주입) =====
  /** 문제집 이름 */
  workbookName: string | null;

  /** 페이지 번호 */
  page: number | null;

  // ===== Actions =====
  /** 모드 변경 */
  setMode: (mode: VisionMode) => void;

  /** 캡처된 이미지 설정 (미리보기 URL 자동 생성) */
  setCapturedImage: (image: File | Blob | null) => void;

  /** Vision API 응답 설정 (editableAnswers 자동 변환) */
  setGradeResponse: (response: VisionGradeResponse) => void;

  /** 개별 답안 수정 */
  updateAnswer: (problemNumber: string, newAnswer: string) => void;

  /** 에러 설정 */
  setError: (error: string | null) => void;

  /** 페이지 정보 설정 */
  setPageInfo: (workbookName: string, page: number) => void;

  /** 확정 응답 설정 */
  setConfirmResponse: (response: VisionConfirmResponse) => void;

  /** 처리 단계 설정 (Phase 5) */
  setProcessingStage: (stage: VisionProcessingStage | null) => void;

  /** 재시도 횟수 증가 */
  incrementRetryCount: () => void;

  /** 압축 결과 설정 */
  setCompressionResult: (result: CompressionResult | null) => void;

  /** 분류된 에러 설정 */
  setLastError: (error: VisionError | null) => void;

  /** 문제를 채팅 채점으로 보류 */
  deferProblem: (problemNumber: string) => void;

  /** 보류된 문제 취소 */
  undeferProblem: (problemNumber: string) => void;

  /** 스토어 초기화 (세션 종료 시) */
  reset: () => void;
}

/**
 * 초기 상태 값
 */
const initialState = {
  mode: 'idle' as VisionMode,
  capturedImage: null,
  imagePreviewUrl: null,
  gradeResponse: null,
  confirmResponse: null,
  editableAnswers: [],
  deferredProblems: [] as string[],
  error: null,
  lastError: null,
  processingStage: null,
  retryCount: 0,
  compressionResult: null,
  workbookName: null,
  page: null,
};

/**
 * Vision 스토어
 *
 * @example
 * ```typescript
 * // 컴포넌트에서 사용
 * const { mode, capturedImage, setMode, setCapturedImage } = useVisionStore();
 *
 * // 이미지 캡처 시
 * const handleCapture = (file: File) => {
 *   setCapturedImage(file);
 *   // mode가 자동으로 'previewing'으로 변경됨
 * };
 *
 * // 스토어 초기화
 * const handleExit = () => {
 *   reset();
 * };
 * ```
 */
export const useVisionStore = create<VisionStoreState>((set, get) => ({
  ...initialState,

  /**
   * 모드 변경
   * @param mode - 새로운 모드
   */
  setMode: (mode: VisionMode) => {
    set({ mode });
  },

  /**
   * 캡처된 이미지 설정
   *
   * - 이전 preview URL이 있으면 메모리 해제 (URL.revokeObjectURL)
   * - 새 이미지로 preview URL 생성
   * - 이미지가 있으면 모드를 'previewing'으로 자동 변경
   *
   * @param image - 이미지 파일 또는 null (취소 시)
   */
  setCapturedImage: (image: File | Blob | null) => {
    // 이전 preview URL 메모리 해제
    const prevUrl = get().imagePreviewUrl;
    if (prevUrl) {
      URL.revokeObjectURL(prevUrl);
    }

    // 새 preview URL 생성
    const imagePreviewUrl = image ? URL.createObjectURL(image) : null;

    set({
      capturedImage: image,
      imagePreviewUrl,
      // 이미지가 있으면 previewing, 없으면 capturing으로
      mode: image ? 'previewing' : 'capturing',
    });
  },

  /**
   * Vision API 응답 설정
   *
   * RecognizedAnswer를 EditableAnswer로 변환하여 저장
   * 모드를 'reviewing'으로 자동 변경
   *
   * @param response - Vision API 채점 응답
   */
  setGradeResponse: (response: VisionGradeResponse) => {
    // RecognizedAnswer → EditableAnswer 변환
    const editableAnswers: EditableAnswer[] = response.recognized_answers.map(
      (ans: RecognizedAnswer) => ({
        ...ans,
        // 수정 전에는 인식된 답안을 그대로 사용
        edited_answer: ans.recognized_answer,
        // 아직 수정되지 않음
        is_edited: false,
      })
    );

    set({
      gradeResponse: response,
      editableAnswers,
      mode: 'reviewing',
      error: null, // 에러 초기화
    });
  },

  /**
   * 개별 답안 수정
   *
   * 해당 문제의 edited_answer를 업데이트하고
   * 원본과 다르면 is_edited를 true로 설정
   *
   * @param problemNumber - 수정할 문제 번호
   * @param newAnswer - 새로운 답안
   */
  updateAnswer: (problemNumber: string, newAnswer: string) => {
    set((state) => ({
      editableAnswers: state.editableAnswers.map((ans) =>
        ans.problem_number === problemNumber
          ? {
              ...ans,
              edited_answer: newAnswer,
              // 원본 인식 결과와 다르면 수정됨으로 표시
              is_edited: newAnswer !== ans.recognized_answer,
            }
          : ans
      ),
    }));
  },

  /**
   * 에러 설정
   * @param error - 에러 메시지 또는 null (에러 해제)
   */
  setError: (error: string | null) => {
    set({ error });
  },

  /**
   * 페이지 정보 설정
   *
   * SolvePage에서 사진 모드 진입 시 호출
   *
   * @param workbookName - 문제집 이름
   * @param page - 페이지 번호
   */
  setPageInfo: (workbookName: string, page: number) => {
    set({ workbookName, page });
  },

  /**
   * 확정 응답 설정
   * @param response - 채점 확정 응답
   */
  setConfirmResponse: (response: VisionConfirmResponse) => {
    set({ confirmResponse: response });
  },

  /**
   * 처리 단계 설정 (Phase 5)
   *
   * 로딩 UI에서 현재 진행 상태를 표시하기 위해 사용
   *
   * @param stage - 현재 처리 단계 ('compressing' | 'uploading' | 'analyzing' | 'grading')
   */
  setProcessingStage: (stage: VisionProcessingStage | null) => {
    set({ processingStage: stage });
  },

  /**
   * 재시도 횟수 증가
   *
   * API 호출 실패 후 재시도 시 호출
   */
  incrementRetryCount: () => {
    set((state) => ({ retryCount: state.retryCount + 1 }));
  },

  /**
   * 압축 결과 설정
   *
   * 이미지 압축 완료 후 결과 메타데이터 저장
   *
   * @param result - 압축 결과 (원본/압축 크기, 압축률 등)
   */
  setCompressionResult: (result: CompressionResult | null) => {
    set({ compressionResult: result });
  },

  /**
   * 분류된 에러 설정
   *
   * 에러 발생 시 재시도/fallback 판단을 위해 사용
   *
   * @param error - 분류된 VisionError 또는 null (에러 해제)
   */
  setLastError: (error: VisionError | null) => {
    set({
      lastError: error,
      error: error?.message || null,
    });
  },

  /**
   * 문제를 채팅 채점으로 보류
   *
   * 인식 실패 또는 학생이 수정한 문제를 Vision 채점에서 제외하고
   * 채팅 모드로 풀기 위해 보류
   *
   * @param problemNumber - 보류할 문제 번호
   */
  deferProblem: (problemNumber: string) => {
    set((state) => ({
      deferredProblems: state.deferredProblems.includes(problemNumber)
        ? state.deferredProblems
        : [...state.deferredProblems, problemNumber],
    }));
  },

  /**
   * 보류된 문제 취소
   *
   * 채팅 채점으로 보류했던 문제를 다시 Vision 채점에 포함
   *
   * @param problemNumber - 취소할 문제 번호
   */
  undeferProblem: (problemNumber: string) => {
    set((state) => ({
      deferredProblems: state.deferredProblems.filter((p) => p !== problemNumber),
    }));
  },

  /**
   * 스토어 초기화
   *
   * 세션 종료 또는 모드 전환 시 호출
   * 메모리 누수 방지를 위해 preview URL 해제
   */
  reset: () => {
    // 메모리 누수 방지: preview URL 해제
    const prevUrl = get().imagePreviewUrl;
    if (prevUrl) {
      URL.revokeObjectURL(prevUrl);
    }

    set(initialState);
  },
}));
